import {useState} from 'react'
import useFetch from 'shared/model/useFetch'
import useAlertModalAtom from 'shared/model/useAlertModalAtom'
import useRoleCookie from 'shared/model/useRoleCookie'
import getApiRolePath from 'shared/utils/getApiRolePath'

const useVoucherType = () => {
	const [createFetch, createRequest] = useFetch()
	const [updateFetch, updateRequest] = useFetch()
	const [deleteFetch, deleteRequest] = useFetch()
	const [, setAlert] = useAlertModalAtom()
	const [userRole] = useRoleCookie()
	const rolePath = getApiRolePath(userRole)
	const [isProcessing, setIsProcessing] = useState(false)

	const processAllChanges = async (originalData, currentVoucherList) => {
		// Validate
		const hasEmptyName = currentVoucherList.some(
			(voucher) => !voucher.name?.trim()
		)
		if (hasEmptyName) {
			setAlert('모든 매장 이용권 유형의 이름을 입력해주세요.')
			return false
		}

		const names = currentVoucherList
			.map((v) => v.name?.trim().toLowerCase())
			.filter(Boolean)
		const duplicateNames = names.filter(
			(name, index) => names.indexOf(name) !== index
		)
		if (duplicateNames.length > 0) {
			setAlert('매장 이용권 유형 이름이 중복되었습니다.')
			return false
		}

		setIsProcessing(true)

		try {
			const operations = calculateOperations(originalData, currentVoucherList)

			if (operations.length === 0) {
				setAlert('변경사항이 없습니다.')
				return true
			}

			for (const operation of operations) {
				await executeOperation(operation)
				await new Promise((resolve) => setTimeout(resolve, 300))
			}

			return true
		} catch (error) {
			console.error('Error processing changes:', error)
			setAlert(error.message || '업데이트 중 오류가 발생했습니다.')
			return false
		} finally {
			setIsProcessing(false)
		}
	}

	const calculateOperations = (original, current) => {
		const operations = []
		const originalMap = new Map(original?.map((item) => [item.idx, item]) || [])
		const currentMap = new Map()

		current.forEach((voucher) => {
			if (voucher.idx) {
				currentMap.set(voucher.idx, voucher)
			}
		})

		originalMap.forEach((item, idx) => {
			if (!currentMap.has(idx)) {
				operations.push({type: 'DELETE', data: item})
			}
		})

		currentMap.forEach((currentItem, idx) => {
			const originalItem = originalMap.get(idx)
			if (
				originalItem &&
				(originalItem.name !== currentItem.name ||
					originalItem.expiration_days !== currentItem.expiration_days)
			) {
				operations.push({type: 'UPDATE', data: currentItem})
			}
		})

		current.forEach((voucher) => {
			if (!voucher.idx) {
				operations.push({type: 'CREATE', data: voucher})
			}
		})

		return operations
	}

	const executeOperation = async (operation) => {
		const {type, data} = operation

		let response

		switch (type) {
			case 'CREATE':
				response = await createRequest(
					'POST',
					`/${rolePath}/store/ticket-types`,
					{
						name: data.name.trim(),
						expiration_days: data.expiration_days,
					}
				)

				if (response.statusCode === 409) {
					const errorMessage =
						response.data?.message || '이미 존재하는 티켓 타입 이름입니다'
					throw new Error(errorMessage)
				} else if (response.statusCode !== 201 && response.statusCode !== 200) {
					const errorMessage =
						response.data?.message || `CREATE failed: ${response.statusCode}`
					throw new Error(errorMessage)
				}
				break

			case 'UPDATE':
				response = await updateRequest(
					'PUT',
					`/${rolePath}/store/ticket-types/${data.idx}`,
					{
						name: data.name.trim(),
						expiration_days: data.expiration_days,
					}
				)

				if (response.statusCode === 409) {
					const errorMessage =
						response.data?.message || '이미 존재하는 티켓 타입 이름입니다'
					throw new Error(errorMessage)
				} else if (response.statusCode !== 200) {
					const errorMessage =
						response.data?.message || `UPDATE failed: ${response.statusCode}`
					throw new Error(errorMessage)
				}
				break

			case 'DELETE':
				response = await deleteRequest(
					'DELETE',
					`/${rolePath}/store/ticket-types/${data.idx}`
				)

				if (response.statusCode !== 200) {
					const errorMessage =
						response.data?.message || `DELETE failed: ${response.statusCode}`
					throw new Error(errorMessage)
				}
				break

			default:
				throw new Error(`Unknown operation type: ${type}`)
		}

		return response
	}

	return [processAllChanges, isProcessing]
}

export default useVoucherType
